import type {
  ReportDataset, DatasetMeta, DatasetOptions, ReportFilter,
  ReportRow, ReportColumn, ComparisonData, ChartType,
  WorkflowId, MetricId, DimensionId,
} from '../types'
import { getCompatibleCharts } from './charts'
import { METRIC_REGISTRY }     from '../registry/metrics'

// ── Empty dataset builder ─────────────────────────────────────────────────────

export function emptyDataset(
  providerId: string,
  workflowId: WorkflowId | null,
  filter:     ReportFilter,
  options:    DatasetOptions = {},
): ReportDataset {
  return {
    meta: {
      providerId,
      workflowId,
      generatedAt: new Date().toISOString(),
      filter,
      options,
      rowCount:    0,
      metricCount: 0,
    },
    rows:        [],
    columns:     [],
    metrics:     {},
    totals:      {},
    comparison:  undefined,
    chartHints:  [],
    exportReady: false,
  }
}

// ── Metric-only dataset (flat aggregates from dashboard APIs) ─────────────────

export function metricsDataset(
  providerId:  string,
  workflowId:  WorkflowId | null,
  filter:      ReportFilter,
  metrics:     Record<string, number>,
  comparison?: ComparisonData,
  options:     DatasetOptions = {},
): ReportDataset {
  const metricKeys    = Object.keys(metrics)
  const metricEntries = metricKeys
    .map((k) => METRIC_REGISTRY[k as MetricId])
    .filter(Boolean)

  const categories = [...new Set(metricEntries.map((m) => m.category))]
  const chartHints = getCompatibleCharts(categories, false, 0)
    .map((c) => c.type)
    .slice(0, 3) as ChartType[]

  const meta: DatasetMeta = {
    providerId,
    workflowId,
    generatedAt: new Date().toISOString(),
    filter,
    options,
    rowCount:    0,
    metricCount: metricKeys.length,
  }

  return {
    meta,
    rows:        [],
    columns:     [],
    metrics,
    totals:      metrics,
    comparison,
    chartHints,
    exportReady: metricKeys.length > 0,
  }
}

// ── Row dataset (future: time-series, per-agent, per-team) ────────────────────

export function rowDataset(
  providerId:  string,
  workflowId:  WorkflowId | null,
  filter:      ReportFilter,
  rows:        ReportRow[],
  columns:     ReportColumn[],
  comparison?: ComparisonData,
  options:     DatasetOptions = {},
): ReportDataset {
  // Compute totals from rows for metric columns
  const totals: Record<string, number> = {}
  const metricColumns = columns.filter((c) => c.type === 'metric')
  for (const col of metricColumns) {
    totals[col.id] = rows.reduce((sum, row) => sum + (row.metrics[col.id] ?? 0), 0)
  }

  // Flat metrics = totals
  const metrics = { ...totals }

  // Determine compatible dimensions from rows
  const usedDimensions = rows.length > 0
    ? (Object.keys(rows[0]!.dimensions) as DimensionId[])
    : []
  const metricEntries = metricColumns
    .map((c) => METRIC_REGISTRY[c.id as MetricId])
    .filter(Boolean)
  const categories = [...new Set(metricEntries.map((m) => m.category))]
  const chartHints = getCompatibleCharts(categories, usedDimensions.length > 0, rows.length)
    .map((c) => c.type) as ChartType[]

  return {
    meta: {
      providerId,
      workflowId,
      generatedAt: new Date().toISOString(),
      filter,
      options,
      rowCount:    rows.length,
      metricCount: metricColumns.length,
    },
    rows,
    columns,
    metrics,
    totals,
    comparison,
    chartHints,
    exportReady: rows.length > 0 || Object.keys(metrics).length > 0,
  }
}
